/**
 * Admin User Management Page
 * Manage and view all admin users
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useDebounce } from '../../hooks';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableHead,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { useTheme, Theme } from '@mui/material/styles';
import {
  MoreVert as MoreVertIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { MainLayout } from '../../components/layout/MainLayout';
import { LoadingSpinner, EmptyState, TablePagination } from '../../components/common';
import { Card, CardContent, TableCell, TableHeaderCell, TableRow, TableContainer, SearchBox, Select, PrimaryButton, SecondaryButton } from '../../components/ui';
import { AdminUserService } from '../../services/adminUser/AdminUserService';
import { canManageAdminUsers } from '../../utils/permissions';
import { useMessage } from '../../contexts/MessageContext';
import type {
  AdminUser,
  AdminUserStatus,
  AdminUserRole,
  PaginatedAdminUsers,
} from '../../types/adminUser';
import {
  adminUserRoleLabels,
  adminUserStatusLabels,
} from '../../types/adminUser';

type StatusFilter = AdminUserStatus | 'all';
type RoleFilter = AdminUserRole | 'all';

const statusFilters: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'disabled', label: 'Disabled' },
];

const roleFilters: { value: RoleFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'super_admin', label: 'Super Admin' },
  { value: 'support', label: 'Support' }
];

const PROTECTED_ADMIN_EMAIL = 'admin@mispy.ai';

const getStatusDotColor = (status: AdminUserStatus, theme: Theme): string => {
  switch (status) {
    case 'active':
      return theme.palette.success.main;
    case 'disabled':
      return theme.palette.error.main;
    default:
      return theme.palette.text.secondary;
  }
};

const isProtectedAdminUser = (user: AdminUser | null): boolean =>
  user?.email?.toLowerCase() === PROTECTED_ADMIN_EMAIL;

const AdminUserManagementPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { showError } = useMessage();
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchValue, setSearchValue] = useState('');
  const debouncedSearchValue = useDebounce(searchValue, 500);
  const [activeStatus, setActiveStatus] = useState<StatusFilter>('all');
  const [activeRole, setActiveRole] = useState<RoleFilter>('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalResults, setTotalResults] = useState(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<AdminUser | null>(null);

  // Check permission
  useEffect(() => {
    if (!canManageAdminUsers()) {
      navigate('/', { replace: true });
    }
  }, [navigate]);

  const fetchAdminUsers = useCallback(async () => {
    try {
      setIsLoading(true);
      const result: PaginatedAdminUsers = await AdminUserService.getAdminUsers(
        page,
        pageSize,
        {
          status: activeStatus,
          role: activeRole,
          search: debouncedSearchValue || undefined,
        }
      );
      setAdminUsers(result.data);
      setTotalResults(result.total);
    } catch (error) {
      console.error('Failed to fetch admin users:', error);
      setAdminUsers([]);
    } finally {
      setIsLoading(false);
    }
  }, [page, pageSize, activeStatus, activeRole, debouncedSearchValue]);

  useEffect(() => {
    fetchAdminUsers();
  }, [fetchAdminUsers]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
    setPage(1);
  };

  const handleStatusChange = (status: StatusFilter) => {
    setActiveStatus(status);
    setPage(1);
  };

  const handleRoleChange = (role: RoleFilter) => {
    setActiveRole(role);
    setPage(1);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, user: AdminUser) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuExited = () => {
    setSelectedUser(null);
  };

  const handleAddAdmin = () => {
    navigate('/settings/admin-users/add');
  };

  const handleViewAdmin = (user: AdminUser) => {
    // Navigate to edit page with view mode
    navigate(`/settings/admin-users/edit/${user.id}?mode=view`);
    handleMenuClose();
  };

  const handleEditAdmin = (user: AdminUser) => {
    if (isProtectedAdminUser(user)) {
      handleMenuClose();
      return;
    }

    navigate(`/settings/admin-users/edit/${user.id}`);
    handleMenuClose();
  };

  const handlePageSizeChange = (event: React.ChangeEvent<unknown>) => {
    // MUI Select casts the event nicely, but handle it carefully depending on exact import
    const val = (event.target as any).value;
    setPageSize(Number(val));
    setPage(1);
  };

  const handleDeleteAdmin = (user: AdminUser) => {
    if (isProtectedAdminUser(user)) {
      handleMenuClose();
      return;
    }

    setUserToDelete(user);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;

    try {
      await AdminUserService.deleteAdminUser(userToDelete.id);
      // Refresh the admin users list
      fetchAdminUsers();
    } catch (error: any) {
      console.error('Failed to delete admin user:', error);
      showError(error.message);
    } finally {
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setUserToDelete(null);
  };

  const getAvatarInitials = (name: string): string => {
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name[0].toUpperCase();
  };

  const isSelectedUserProtected = isProtectedAdminUser(selectedUser);
  const protectedActionMenuItemSx = {
    '&.Mui-disabled': {
      color: theme.palette.text.disabled,
      opacity: 1,
    },
  };

  return (
    <MainLayout>


      {/* Page Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: theme.spacing(3),
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: theme.palette.text.primary,
          }}
        >
          Admin User Management
        </Typography>
        <PrimaryButton
          startIcon={<AddIcon />}
          onClick={handleAddAdmin}
          sx={{ padding: theme.spacing(1, 2) }}
        >
          Add New Admin
        </PrimaryButton>
      </Box>

      {/* Search and Filters */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: theme.spacing(3),
          flexWrap: 'wrap',
          gap: theme.spacing(2),
        }}
      >
        {/* Search Box */}
        <SearchBox
          placeholder="Search by name, email"
          value={searchValue}
          onChange={handleSearch}
          containerSx={{ flex: 1, minWidth: 300 }}
        />

        {/* Status Filter */}
        <Select
          value={activeStatus}
          onChange={(e) => handleStatusChange(e.target.value as StatusFilter)}
          displayEmpty
          size="small"
          renderValue={(value) => {
            const filter = statusFilters.find((f) => f.value === value);
            return `Status: ${filter?.label || 'All'}`;
          }}
          formControlProps={{
            sx: {
              minWidth: 150,
            },
          }}
          sx={{
            color: theme.palette.text.primary,
            '& .MuiSelect-select': {
              padding: theme.spacing(1.5, 2),
            },
          }}
        >
          {statusFilters.map((filter) => (
            <MenuItem key={filter.value} value={filter.value}>
              {filter.label}
            </MenuItem>
          ))}
        </Select>

        {/* Role Filter */}
        <Select
          value={activeRole}
          onChange={(e) => handleRoleChange(e.target.value as RoleFilter)}
          displayEmpty
          size="small"
          renderValue={(value) => {
            const filter = roleFilters.find((f) => f.value === value);
            return `Role: ${filter?.label || 'All'}`;
          }}
          formControlProps={{
            sx: {
              minWidth: 150,
            },
          }}
          sx={{
            color: theme.palette.text.primary,
            '& .MuiSelect-select': {
              padding: theme.spacing(1.5, 2),
            },
          }}
        >
          {roleFilters.map((filter) => (
            <MenuItem key={filter.value} value={filter.value}>
              {filter.label}
            </MenuItem>
          ))}
        </Select>
      </Box>

      {/* Admin Users Table */}
      <Card>
        <CardContent noPadding>
          {isLoading ? (
            <Box sx={{ padding: theme.spacing(6), display: 'flex', justifyContent: 'center' }}>
              <LoadingSpinner />
            </Box>
          ) : adminUsers.length === 0 ? (
            <Box sx={{ padding: theme.spacing(4) }}>
              <EmptyState
                title="No Admin Users Found"
                message="There are no admin users in the system."
              />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table sx={{ minWidth: 900 }}>
                  <TableHead>
                    <TableRow>
                      <TableHeaderCell>Name</TableHeaderCell>
                      <TableHeaderCell>Email</TableHeaderCell>
                      <TableHeaderCell>Role</TableHeaderCell>
                      <TableHeaderCell>Status</TableHeaderCell>
                      <TableHeaderCell>Last Login</TableHeaderCell>
                      <TableHeaderCell>Actions</TableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {adminUsers.map((user, index) => (
                      <TableRow key={`${user.id}-${index}`}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1.5) }}>
                            <Avatar
                              sx={{
                                width: 40,
                                height: 40,
                                backgroundColor: theme.palette.primary.main,
                                fontSize: theme.typography.body2.fontSize,
                              }}
                              src={user.avatar}
                            >
                              {!user.avatar && getAvatarInitials(user.name || '')}
                            </Avatar>
                            <Typography
                              variant="body2"
                              sx={{
                                color: theme.palette.text.primary,
                                fontWeight: 500,
                                fontSize: theme.typography.body2.fontSize,
                              }}
                            >
                              {user.name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {user.email}
                        </TableCell>
                        <TableCell>
                          {adminUserRoleLabels[user.role]}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                backgroundColor: getStatusDotColor(user.status, theme),
                              }}
                            />
                            <Typography
                              variant="body2"
                              sx={{
                                color: theme.palette.text.primary,
                                fontSize: theme.typography.body2.fontSize,
                              }}
                            >
                              {adminUserStatusLabels[user.status]}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ color: theme.palette.text.secondary }}>
                          {user.lastLoginText || 'Never'}
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuOpen(e, user)}
                            sx={{ color: theme.palette.text.secondary }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination - Moved outside TableContainer to keep it fixed */}
              <TablePagination
                page={page}
                pageSize={pageSize}
                totalItems={totalResults}
                onPageChange={setPage}
                onPageSizeChange={handlePageSizeChange}
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        TransitionProps={{
          onExited: handleMenuExited,
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={() => selectedUser && handleViewAdmin(selectedUser)}>
          View Details
        </MenuItem>
        <MenuItem
          onClick={() => selectedUser && handleEditAdmin(selectedUser)}
          disabled={isSelectedUserProtected}
          sx={protectedActionMenuItemSx}
        >
          Edit Admin
        </MenuItem>
        <MenuItem
          onClick={() => selectedUser && handleDeleteAdmin(selectedUser)}
          disabled={isSelectedUserProtected}
          sx={{
            color: theme.palette.error.main,
            ...protectedActionMenuItemSx,
          }}
        >
          Delete Admin
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Delete Admin User
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete admin user "{userToDelete?.name}" ({userToDelete?.email})?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <SecondaryButton onClick={handleCancelDelete}>
            Cancel
          </SecondaryButton>
          <PrimaryButton
            onClick={handleConfirmDelete}
            sx={{ backgroundColor: theme.palette.error.main, '&:hover': { backgroundColor: theme.palette.error.dark } }}
            autoFocus
          >
            Delete
          </PrimaryButton>
        </DialogActions>
      </Dialog>
    </MainLayout>
  );
};

export default AdminUserManagementPage;

